import { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../../utils/useTranslation";
import { Controller } from "react-hook-form";
import {
  StyleSheet,
  TouchableOpacity,
  TextInput,
  FlatList,
  ActivityIndicator,
  View,
  Text,
  Keyboard,
} from "react-native";
import { getChinaShipmentsReferences } from "../../../redux/dispatcher/chinaTrackerDispatcher";

const ChinaItemCodeAutocomplete = ({
  index,
  control,
  errors,
  setValue,
  onOpen,
  onClose,
}: {
  index: number;
  control: any;
  errors: any;
  setValue: any;
  onOpen: () => void;
  onClose: () => void;
}) => {
  const dispatch = useDispatch<any>();
  const { t } = useTranslation();

  const [showDropdown, setShowDropdown] = useState(false);
  const [localSuggestions, setLocalSuggestions] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Prevent blur from closing dropdown when a suggestion tap is in-flight
  const selectingRef = useRef(false);
  const currentSearchRef = useRef<string>("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { loading: searching, totalPages } = useSelector(
    (state: any) => state.chinaTracker.shipments.shipmentReferences,
  );

  const itemError = (field: string) => errors?.items?.[index]?.[field]?.message;

  // ── Open / close ─────────────────────────────────────────────────────────────
  const openDropdown = useCallback(() => {
    setShowDropdown(true);
    onOpen();
  }, [onOpen]);

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
    setLocalSuggestions([]);
    setHasMore(false);
    setCurrentPage(1);
    onClose();
  }, [onClose]);

  // ── Fetch page ───────────────────────────────────────────────────────────────
  const fetchPage = useCallback(
    async (search: string, page: number, replace: boolean) => {
      const result = await dispatch(
        getChinaShipmentsReferences({
          page,
          pageSize: 10,
          sort: null,
          status: "",
          search,
          dateFrom: null,
          dateTo: null,
        }),
      );

      if (result.error) return;

      const incoming = result.payload?.data ?? [];
      const pagination = result.payload?.pagination;

      setLocalSuggestions((prev) =>
        replace ? incoming : [...prev, ...incoming],
      );
      setCurrentPage(page);
      setHasMore(page < (pagination?.totalPages ?? totalPages ?? 1));

      if (replace && incoming.length > 0) openDropdown();
      if (replace && incoming.length === 0) closeDropdown();
    },
    [dispatch, totalPages, openDropdown, closeDropdown],
  );

  // ── Debounced search ─────────────────────────────────────────────────────────
  const handleSearch = useCallback(
    (text: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      currentSearchRef.current = text;

      if (!text || text.trim().length < 1) {
        closeDropdown();
        return;
      }

      debounceRef.current = setTimeout(() => {
        fetchPage(text.trim(), 1, true);
      }, 350);
    },
    [fetchPage, closeDropdown],
  );

  // ── Infinite scroll ──────────────────────────────────────────────────────────
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore || searching) return;
    setLoadingMore(true);
    fetchPage(currentSearchRef.current.trim(), currentPage + 1, false).finally(
      () => setLoadingMore(false),
    );
  }, [loadingMore, hasMore, searching, currentPage, fetchPage]);

  // ── Select ───────────────────────────────────────────────────────────────────
  const handleSelect = useCallback(
    (ref: any) => {
      selectingRef.current = true; // block blur close
      Keyboard.dismiss();

      const base = `items.${index}`;
      setValue(`${base}.chinaItemCode`, ref.cnItemCode, {
        shouldValidate: true,
      });
      setValue(`${base}.material`, ref.material ?? "", {
        shouldValidate: true,
      });
      setValue(`${base}.spec`, ref.spec ?? "", { shouldValidate: true });
      setValue(`${base}.qtyPerCarton`, ref.qtyPerUnit ?? 0, {
        shouldValidate: true,
      });
      setValue(`${base}.gwPerCarton`, ref.grossPerUnit ?? 0, {
        shouldValidate: true,
      });
      setValue(`${base}.nwPerCarton`, ref.netPerUnit ?? 0, {
        shouldValidate: true,
      });
      setValue(`${base}.label`, ref.feature || "", {
        shouldValidate: true,
      });
      setValue(`${base}.itemDescription`, ref.cnItemName ?? "", {
        shouldValidate: true,
      });

      closeDropdown();

      // Reset guard after frame
      setTimeout(() => {
        selectingRef.current = false;
      }, 100);
    },
    [index, setValue, closeDropdown],
  );

  // ── Blur — only close if user didn't just tap a suggestion ──────────────────
  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (!selectingRef.current) closeDropdown();
    }, 200);
  }, [closeDropdown]);

  // ── FlatList renderItem ──────────────────────────────────────────────────────
  const renderItem = useCallback(
    ({ item: ref }: { item: any }) => (
      <TouchableOpacity
        style={autoStyles.suggestionRow}
        onPressIn={() => {
          selectingRef.current = true;
        }} // set BEFORE blur fires
        onPress={() => handleSelect(ref)}
        activeOpacity={0.7}
      >
        <Text style={autoStyles.suggestionCode}>{ref.cnItemCode}</Text>
        <Text style={autoStyles.suggestionMeta} numberOfLines={1}>
          {[ref.cnItemName, ref.material, ref.spec].filter(Boolean).join(" · ")}
        </Text>
      </TouchableOpacity>
    ),
    [handleSelect],
  );

  const ListFooter = useCallback(
    () =>
      loadingMore ? (
        <View style={autoStyles.footerRow}>
          <ActivityIndicator size="small" color="#4dabf7" />
          <Text style={autoStyles.footerText}>Loading more…</Text>
        </View>
      ) : !hasMore && localSuggestions.length > 0 ? (
        <View style={autoStyles.footerRow}>
          <Text style={autoStyles.endText}>
            {localSuggestions.length} result
            {localSuggestions.length !== 1 ? "s" : ""}
          </Text>
        </View>
      ) : null,
    [loadingMore, hasMore, localSuggestions.length],
  );

  return (
    // zIndex here ensures the inline dropdown layers above sibling fields
    <View
      style={[autoStyles.wrapper, showDropdown && autoStyles.wrapperActive]}
    >
      <Text style={autoStyles.label}>
        {t("chinaItemCode")} <Text style={autoStyles.asterisk}>*</Text>
      </Text>

      {/* ── Input ── */}
      <View style={autoStyles.inputWrap}>
        <Controller
          control={control}
          name={`items.${index}.chinaItemCode`}
          rules={{ required: "China Item Code is required" }}
          render={({ field: { onChange, value } }) => (
            <TextInput
              style={[
                autoStyles.input,
                !!itemError("chinaItemCode") && autoStyles.inputError,
              ]}
              value={value}
              onChangeText={(text) => {
                onChange(text);
                handleSearch(text);
              }}
              onBlur={handleBlur}
              placeholder="Search CN item code…"
              placeholderTextColor="#bbb"
              autoCorrect={false}
              autoCapitalize="characters"
            />
          )}
        />
        {searching && !loadingMore && (
          <ActivityIndicator
            size="small"
            color="#4dabf7"
            style={autoStyles.spinner}
          />
        )}
      </View>

      {!!itemError("chinaItemCode") && (
        <Text style={autoStyles.errorText}>{itemError("chinaItemCode")}</Text>
      )}

      {/* ── Inline dropdown — no Modal, no clipping issues ── */}
      {showDropdown && (
        <View style={autoStyles.dropdown}>
          {localSuggestions.length === 0 && !searching ? (
            <View style={autoStyles.emptyRow}>
              <Text style={autoStyles.emptyText}>No results found.</Text>
            </View>
          ) : (
            <FlatList
              data={localSuggestions}
              keyExtractor={(item, i) => `${item._id}-${i}`}
              renderItem={renderItem}
              ListFooterComponent={ListFooter}
              onEndReached={handleLoadMore}
              onEndReachedThreshold={0.3}
              keyboardShouldPersistTaps="handled"
              style={autoStyles.flatList}
              // Stop FlatList scroll from bubbling to parent ScrollView
              nestedScrollEnabled
            />
          )}
        </View>
      )}
    </View>
  );
};

export default ChinaItemCodeAutocomplete;

const autoStyles = StyleSheet.create({
  wrapper: {
    flex: 1,
    // default: no special z
  },
  wrapperActive: {
    zIndex: 9999,
    elevation: 9999, // Android
  },
  label: {
    fontSize: 12,
    color: "#444",
    marginBottom: 4,
    fontWeight: "500",
  },
  asterisk: {
    color: "#e03131",
    fontWeight: "700",
  },
  inputWrap: {
    position: "relative",
    justifyContent: "center",
  },
  input: {
    height: 34,
    borderWidth: 1,
    borderColor: "#d4d4d4",
    borderRadius: 6,
    backgroundColor: "#f9f9f9",
    paddingHorizontal: 10,
    paddingRight: 32,
    fontSize: 13,
    color: "#111",
  },
  inputError: {
    borderColor: "#e03131",
    backgroundColor: "#fff5f5",
  },
  errorText: {
    fontSize: 11,
    color: "#e03131",
    marginTop: 2,
  },
  spinner: {
    position: "absolute",
    right: 8,
  },
  dropdown: {
    // Render inline below the input — no absolute positioning needed
    // Parent card's overflow:hidden is handled by zIndex stacking
    marginTop: 2,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#d4d4d4",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 9999,
    overflow: "hidden",
  },
  flatList: {
    maxHeight: 220,
  },
  suggestionRow: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: "#eee",
  },
  suggestionCode: {
    fontSize: 13,
    fontWeight: "600",
    color: "#111",
  },
  suggestionMeta: {
    fontSize: 11,
    color: "#666",
    marginTop: 2,
  },
  footerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 10,
    borderTopWidth: 0.5,
    borderTopColor: "#eee",
    backgroundColor: "#fafafa",
  },
  footerText: { fontSize: 12, color: "#4dabf7" },
  endText: { fontSize: 11, color: "#aaa" },
  emptyRow: { padding: 16, alignItems: "center" },
  emptyText: { fontSize: 13, color: "#999" },
});
